'use client';
import React, { useEffect, useState, useTransition } from 'react';
import { useRouter } from 'next/navigation';
import { Column, FlexGrid, Row } from '@carbon/react';

import { StarButton } from '@/components/SocialButtons/StarButtonComponent';
import { SocialButton } from '@/components/SocialButtons/SocialButtonComponent';

import {
  deleteContentEngagementDetail,
  getContentEngagementStatus,
  postContentEngagementDetail,
} from '@/apis/ContentEngagementAPIs';
import { checkAuthentication, isAuthenticated } from '@/utils/helper';

import Notification from '../Notification';

import styles from './SocialActionsSection.module.scss';

type NotificationType = 'info' | 'success' | 'error';

interface NotificationState {
  message: string;
  type: NotificationType;
  isVisible: boolean;
}

interface SocialActionsSectionProps {
  slug: string;
  socialUrls?: {
    facebook?: string;
    twitter?: string;
    linkedin?: string;
  };
  contentType?: string;
  isEvent?: boolean;
}

export default function SocialActionsSection({
  slug,
  socialUrls = {},
  contentType = '',
  isEvent = false,
}: SocialActionsSectionProps) {
  const [isLiked, setIsLiked] = useState(false);
  const [isFavorite, setIsFavorite] = useState(false);

  const [notification, setNotification] = useState<NotificationState>({
    message: '',
    type: 'info',
    isVisible: false,
  });

  const [isPending, startTransition] = useTransition();

  const router = useRouter();

  const fullSlugParts = slug
    ?.replace(/^\/|\/$/g, '') // remove leading/trailing slashes
    ?.split('/') ?? [slug]; // split by "/"

  const newSlug: string = fullSlugParts.at(-1) ?? '';

  async function getData() {
    let engagementStatus = isAuthenticated() ? await getContentEngagementStatus(newSlug) : {};
    engagementStatus = engagementStatus?.body;

    setIsLiked(engagementStatus?.['like'] || false);
    setIsFavorite(engagementStatus?.['favorite'] || false);
  }

  useEffect(() => {
    getData();
  }, [newSlug]);

  // Handle pending action after login redirect
  useEffect(() => {
    const handlePendingAction = async () => {
      if (typeof window === 'undefined') return;

      const pendingAction = sessionStorage.getItem('pendingEngagementAction');
      const pendingSlug = sessionStorage.getItem('pendingEngagementSlug');

      if (pendingAction && pendingSlug === newSlug && isAuthenticated()) {
        // Clear the pending action first to avoid loops
        sessionStorage.removeItem('pendingEngagementAction');
        sessionStorage.removeItem('pendingEngagementSlug');

        // Execute the pending action
        if (pendingAction === 'favorite') {
          await onClick('favorite');
        } else if (pendingAction === 'unfavorite') {
          await onDeleteClick('favorite');
        } else if (pendingAction === 'like') {
          await onClick('like');
        } else if (pendingAction === 'unlike') {
          await onDeleteClick('like');
        }
      }
    };

    handlePendingAction();
  }, [newSlug]);

  const onClick = async (type: string) => {
    let isLoggedIn = checkAuthentication();

    if (isLoggedIn) {
      const postRes = await postContentEngagementDetail(newSlug, contentType, type);
      if (type === 'like') {
        if (postRes?.status === 200) {
          setIsLiked(true);
          setNotification({
            message: 'Content liked',
            type: 'success',
            isVisible: true,
          });
        } else {
          setNotification({
            message: 'An error occured. Try after some time.',
            type: 'error',
            isVisible: true,
          });
        }
      } else if (type === 'favorite') {
        if (postRes?.status === 200) {
          setIsFavorite(true);
          setNotification({
            message: 'Content saved',
            type: 'success',
            isVisible: true,
          });
        } else {
          setNotification({
            message: 'An error occured. Try after some time.',
            type: 'error',
            isVisible: true,
          });
        }
      }
      startTransition(() => {
        router.refresh();
      });
    } else {
      // Store pending action for after login
      sessionStorage.setItem('pendingEngagementAction', type);
      sessionStorage.setItem('pendingEngagementSlug', newSlug);
    }
  };

  const onDeleteClick = async (type: string) => {
    let isLoggedIn = checkAuthentication();
    if (isLoggedIn) {
      const delRes = await deleteContentEngagementDetail(newSlug, contentType, type);
      if (type === 'like') {
        if (delRes?.status === 200) {
          setIsLiked(false);
          setNotification({
            message: 'Content unliked',
            type: 'success',
            isVisible: true,
          });
        } else {
          setNotification({
            message: 'An error occured. Try after some time.',
            type: 'error',
            isVisible: true,
          });
        }
      } else if (type === 'favorite') {
        if (delRes?.status === 200) {
          setIsFavorite(false);
          setNotification({
            message: 'Content unsaved',
            type: 'success',
            isVisible: true,
          });
        } else {
          setNotification({
            message: 'An error occured. Try after some time.',
            type: 'error',
            isVisible: true,
          });
        }
      }
      startTransition(() => {
        router.refresh();
      });
    } else {
      // Store pending action for after login (with 'un' prefix for delete actions)
      sessionStorage.setItem('pendingEngagementAction', `un${type}`);
      sessionStorage.setItem('pendingEngagementSlug', newSlug);
    }
  };

  return (
    <FlexGrid className={styles.socialSection}>
      <div>
        {/* Show Notification component here */}
        <Notification
          message={notification.message}
          type={notification.type}
          isVisible={notification.isVisible}
          onClose={() => {
            setNotification({
              message: '',
              type: 'info',
              isVisible: false,
            });
          }}
        />
      </div>
      <Row>
        <Column sm={4} md={8} lg={16} className="no-margin">
          <div className={styles.socialButtons}>
            <div className={styles.socialSharingLike} style={{ display: 'flex' }}>
              {isEvent === false && (
                <StarButton
                  isFavorite={isFavorite}
                  handleClick={() => onClick('favorite')}
                  handleDeleteClick={() => onDeleteClick('favorite')}
                />
              )}
            </div>

            <div className={styles.socialSharingGroup}>
              <SocialButton socialMediaName="facebook" url={socialUrls.facebook || ''} />
              <SocialButton socialMediaName="twitter" url={socialUrls.twitter || ''} />
              <SocialButton socialMediaName="linkedin" url={socialUrls.linkedin || ''} />
            </div>
          </div>
        </Column>
      </Row>
    </FlexGrid>
  );
}
